import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql, useFragment } from "react-relay";

import { captureException } from "@sentry/react";
import { differenceInSeconds, isAfter, isBefore, isValid } from "date-fns";
import { noop } from "lodash";
import invariant from "tiny-invariant";

import { inVideocall } from "~/lib/browserExtension";
import { hasOwnProperty } from "~/lib/js";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getFirstUserFacingError } from "~/lib/utils";
import { useGroupEmitter } from "~/lib/websocket";
import { useCopilotPaywallsEnabled } from "~/meetingRecorder/featureFlag";
import { useAutoRefetchMeetingRecordings } from "~/meetingRecorder/utils";
import { environment } from "~/models";
import OngoingMeetingContext from "~/OngoingMeetingContext";
import { toast } from "~/ui";

import { useOpenOutOfCreditsModal } from "./NoteHeader/OutOfCreditsModal";
import { autoShareIsSetToEveryone, enableAutoShareToEveryone } from "./SuggestedActions/AutoSendToEveryone";
import { useRecordingErrorIndicatorFeatureFlagOn } from "./utils";

import type {
	MeetingPhaseContext_note$data,
	MeetingPhaseContext_note$key,
	MeetingRecordingProvider,
} from "~/__generated__/MeetingPhaseContext_note.graphql";
import type { MeetingPhaseContext_PauseMeetingRecordingMutation as PauseRecordingMutation } from "~/__generated__/MeetingPhaseContext_PauseMeetingRecordingMutation.graphql";
import type {
	RecordingMediaType,
	MeetingPhaseContext_RecordMeetingMutation as RecordMeetingMutation,
} from "~/__generated__/MeetingPhaseContext_RecordMeetingMutation.graphql";
import type { MeetingPhaseContext_ResumeMeetingRecordingMutation as ResumeRecordingMutation } from "~/__generated__/MeetingPhaseContext_ResumeMeetingRecordingMutation.graphql";
import type { MeetingPhaseContext_StopMeetingRecordingMutation as StopRecordingMutation } from "~/__generated__/MeetingPhaseContext_StopMeetingRecordingMutation.graphql";
import type { MeetingPhaseContext_viewer$key } from "~/__generated__/MeetingPhaseContext_viewer.graphql";

export interface CopilotErrorIndicator {
	type: "generic" | "cancelled";
	message?: string;
	cancelledBy?: {
		id: string;
		fullName: string;
		email: string;
	};
	updatedAt?: Date;
}

type ConferenceCall = NonNullable<MeetingPhaseContext_note$data["event"]>["conferenceCalls"][number];
export type MeetingPhase = "before" | "started" | "after";
type WsMessage =
	| {
			type: "MEETING_RECORDER_STATUS_UPDATED";
			data: {
				meetingRecordingId: string;
				status: string;
				statusCreatedAt: string;
				userFacingErrorMessage: string | null;
				cancelledBy: {
					id: string;
					fullName: string;
					email: string;
				} | null;
			};
	  }
	| { type: "something else" };

export type CopilotStatus =
	| { type: "starting"; statusCreatedAt?: Date }
	| { type: "scheduled"; statusCreatedAt?: Date }
	| { type: "not-recording"; statusCreatedAt?: Date }
	| {
			type: "paused";
			statusCreatedAt?: Date;
			recordingStartedAt: Date;
			mediaType: RecordingMediaType;
			provider: MeetingRecordingProvider;
	  }
	| { type: "waiting"; statusCreatedAt?: Date }
	| { type: "stopping"; statusCreatedAt?: Date }
	| {
			type: "recording";
			statusCreatedAt?: Date;
			recordingStartedAt: Date;
			mediaType: RecordingMediaType;
			provider: MeetingRecordingProvider;
	  }
	| { type: "saved"; statusCreatedAt?: Date }
	| { type: "error"; indicator: CopilotErrorIndicator; statusCreatedAt?: Date };

type MeetingPhaseContextSlice = {
	noMeeting: boolean;

	event: {
		start: Date;
		end: Date;
		isAllDay: boolean;
	};

	phase: MeetingPhase;

	/**
	 * Returns a seconds value whose meaning depends on the phase:
	 * - `before`: The number of seconds until the meeting starts.
	 * - `started`: The number of seconds until the meeting ends.
	 * 							If negative, it's the number of seconds since the meeting should have ended
	 * - `after`: The number of seconds since the meeting should have ended.
	 */
	getTimerValue: () => number;
};

export type { RecordingMediaType };
export type RecordingMode = "BOT" | "BOTLESS";

type CopilotContextSlice = {
	copilotStatus: CopilotStatus;
	startRecording: (
		mode: RecordingMode,
		call: ConferenceCall | null,
		opts?: { overrideAutoRecord?: boolean; mediaType: RecordingMediaType },
	) => Promise<boolean>;
	stopRecording: () => void;
	pauseRecording: () => void;
	resumeRecording: () => void;
	previousErrorIndicator: CopilotErrorIndicator | null;
	hasMeetingRecordings: boolean;
	mustUpgradeBeforeRecording: boolean;
	creator?: { id: string; firstName: string } | null;
};

type ContextValue = MeetingPhaseContextSlice & CopilotContextSlice & { viewerId: string | null };

// this list should match the list in the MeetingInfoView class in the backend.
export const SUPPORTED_CALL_PROVIDERS: readonly ConferenceCall["provider"][] = ["HANGOUTS_MEET", "MS_TEAMS", "ZOOM"];

export function someConferenceCallSupportsRecording(calls: readonly Pick<ConferenceCall, "provider">[]) {
	return calls.some(call => SUPPORTED_CALL_PROVIDERS.includes(call.provider));
}

const REMOTE_STATUS_MAPPING = {
	READY: { type: "starting" },
	IN_WAITING_ROOM: { type: "waiting" },
	JOINED_CALL: { type: "starting" },
	RECORDING_DONE: { type: "not-recording" },
	KICKED_OUT: { type: "not-recording" },
	JOIN_REQUEST_DENIED: { type: "error", indicator: { type: "generic", message: "Note Taker denied entry" } },
	ERROR: { type: "error", indicator: { type: "generic", message: "Something went wrong…" } },
	UNKNOWN: { type: "error", indicator: { type: "generic", message: "Note Taker unavailable" } },
	TIME_OUT_EXCEEDED_WAITING_ROOM: {
		type: "error",
		indicator: { type: "generic", message: "Time in waiting room exceeded" },
	},
	TIME_OUT_EXCEEDED_IN_CALL_NOT_RECORDING: {
		type: "error",
		indicator: { type: "generic", message: "Note Taker denied recording permission" },
	},
	TIME_OUT_EXCEEDED_RECORDING_PERMISSION_DENIED: {
		type: "error",
		indicator: { type: "generic", message: "Note Taker denied recording permission" },
	},
	RECORDING_PERMISSION_DENIED: {
		type: "error",
		indicator: { type: "generic", message: "Note Taker denied recording permission" },
	},
} as const satisfies Record<string, CopilotStatus>;

function notifyExtensionRecordingInProgress() {
	invariant(inVideocall);
	window.parent.postMessage({ type: "MEETING_RECORDER_STARTED" }, "*");
}

const NULL_MEETING: ContextValue = {
	noMeeting: true,

	event: {
		start: new Date(),
		end: new Date(),
		isAllDay: false,
	},

	phase: "before",
	getTimerValue: () => NaN,

	copilotStatus: { type: "not-recording" },
	startRecording: async () => false,
	stopRecording: noop,
	pauseRecording: noop,
	resumeRecording: noop,
	previousErrorIndicator: null,
	viewerId: null,
	hasMeetingRecordings: false,
	mustUpgradeBeforeRecording: true,
	creator: null,
};
const MeetingPhaseContext = React.createContext<ContextValue>(NULL_MEETING);

export default MeetingPhaseContext;

const viewerFragment = graphql`
	fragment MeetingPhaseContext_viewer on Viewer {
		...featureFlag_useCopilotPaywallsOn_viewer

		remainingMeetingRecordingCredits
		hasAccessToCopilot
		hasAccessToScheduledMeetingRecordings
		hasAdminAccess
		account {
			allowMeetingRecording
		}
		user {
			id
		}
		meetingRecapAutoShareSettings {
			id
			sendTo
		}
	}
`;

const noteFragment = graphql`
	fragment MeetingPhaseContext_note on Note {
		id
		groupName
		event {
			id
			title
			status
			start
			end
			isAllDay
			conferenceCalls {
				provider
				url
			}
			hasUsersWithExternalEmail
		}
		finished
		eventGuid
		url
		account {
			allowAiFeatures
		}
		stream {
			id
			schedulingMeetingAssistantAutomationIsActive
		}
		meetingRecordings {
			id
			status
			externalRecordingProvider
			meetingCallStart
			mediaType
			userWhoStartedRecording {
				id
				firstName
			}
		}

		recordingErrorIndicator {
			message
			cancelledBy {
				id
				fullName
				email
			}
			errorType
			updatedAt
		}

		hasMeetingRecordings

		...utils_useAutoRefetchMeetingRecording_note
		...AutoSendToEveryone_autoShareIsSetToEveryone_note
	}
`;

function refetchViewerInBackground() {
	fetchQuery(
		environment,
		graphql`
			query MeetingPhaseContextRefetchViewerQuery {
				viewer {
					id
					...MeetingPhaseContext_viewer
				}
			}
		`,
		{},
		{ fetchPolicy: "network-only" },
	).toPromise();
}

type MeetingPhaseContextProviderProps = React.PropsWithChildren<{
	viewer: MeetingPhaseContext_viewer$key;
	note: MeetingPhaseContext_note$key;
}>;

export const getMeetingPhase = (start: Date | null, end: Date | null, noteFinished: boolean) => {
	if (start === null || end === null) return NULL_MEETING.phase;

	const now = new Date();
	if (noteFinished || isAfter(now, end)) return "after";
	else if (isBefore(now, start)) return "before";
	else return "started";
};

/**
 * Accept the new status if it has a newer statusCreatedAt than the old status.
 * If the old status is newer, it means the new status is out of date
 * and we should ignore it. if only the new status has a statusCreatedAt,
 * it means the status was created after the old status, so we should accept it.
 */
const acceptNewStatus = (oldStatus: CopilotStatus, newStatus: CopilotStatus) => {
	if (oldStatus.statusCreatedAt && newStatus.statusCreatedAt) {
		return newStatus.statusCreatedAt > oldStatus.statusCreatedAt;
	} else if (newStatus.statusCreatedAt) {
		return true;
	}
	return false;
};

export function MeetingPhaseContextProvider({
	children,
	viewer: viewerKey,
	note: noteKey,
}: MeetingPhaseContextProviderProps) {
	const viewer = useFragment(viewerFragment, viewerKey);
	const note = useFragment(noteFragment, noteKey);
	const errorIndicatorsEnabled = useRecordingErrorIndicatorFeatureFlagOn();
	/*
	 * The recordingMode is set when a recording is started to signal bot/botless
	 * and is cleared when the recording is stopped.
	 *
	 * This lets the context know how we are recording a meeting from the start
	 * without relying on a server ping of any kind.
	 */
	const [recordingMode, setRecordingMode] = useState<RecordingMode>();

	const { start, end } = useMemo(() => {
		const event = note.event;
		if (event === null) {
			// This note has no meeting attached
			return { start: null, end: null };
		}

		const start = event.start ? new Date(event.start) : null;
		const end = event.end ? new Date(event.end) : null;

		if (start === null || end === null || !isValid(start) || !isValid(end)) {
			captureException(new Error("Attached event has invalid start or end time."), {
				extra: {
					eventGuid: note.eventGuid,
					noteUrl: note.url,
				},
			});
			return { start: null, end: null };
		}

		return { start, end };
	}, [note.event, note.eventGuid, note.url]);

	const calculatePhase = useCallback(() => getMeetingPhase(start, end, note.finished), [note.finished, start, end]);

	const [phase, setPhase] = useState(calculatePhase);

	// Anything used to calculate the phase will update the callback. We want to
	// 	recalculate the phase when any of those dependencies change.
	useEffect(() => {
		setPhase(calculatePhase());
	}, [calculatePhase]);

	// We currently don't propagate to all our windows the a local recording is stopping
	// So the user don't have a unified experience about the recording status
	// In the future we could look into propagating this to all users in that meeting
	// And if that happens this wouldn't be needed anymore
	useEffect(() => {
		if (!window.desktopRecorder || !window.desktopRecorder.registerLocalRecordingIsStopping) return;
		window.desktopRecorder.registerLocalRecordingIsStopping(() => {
			setCopilotStatus({ type: "stopping" });
		});
		return () => {
			if (!window.desktopRecorder || !window.desktopRecorder.registerLocalRecordingIsStopping) return;
			window.desktopRecorder.registerLocalRecordingIsStopping(undefined);
		};
	}, []);

	const getTimerValue = useCallback(() => {
		if (start === null || end === null) return NULL_MEETING.getTimerValue();

		const now = new Date();

		switch (phase) {
			case "before":
				return differenceInSeconds(start, now);
			case "started":
				return differenceInSeconds(end, now);
			case "after":
				return differenceInSeconds(now, end);
		}
	}, [start, end, phase]);

	const meetingContextValue: MeetingPhaseContextSlice = useMemo(() => {
		if (start === null || end === null) return NULL_MEETING;

		return {
			noMeeting: false,
			event: {
				start,
				end,
				isAllDay: Boolean(note.event?.isAllDay),
			},
			phase,
			getTimerValue,
		};
	}, [start, end, note.event?.isAllDay, phase, getTimerValue]);

	/**
	 * This effect is responsible for switching the phase at the right time.
	 * For "before" -> "started" we want that to happen at the start of the meeting.
	 * For "started" -> "after" we want that to happen an hour after the meeting should have ended to allow for overtime.
	 * The correct phase will still be calculated on render, so the phase can be "after" if the meeting has already ended
	 * 	and the user refreshes the page.
	 */
	useEffect(() => {
		let phaseSwitchTimeout: number;

		const { noMeeting, phase, getTimerValue } = meetingContextValue;
		if (noMeeting || phase === "after") return;

		const secondsUntilNextPhase = getTimerValue();

		switch (phase) {
			case "before": {
				phaseSwitchTimeout = window.setTimeout(() => setPhase("started"), secondsUntilNextPhase * 1000);
				break;
			}
			case "started": {
				const secondsUntilAfterPhase =
					secondsUntilNextPhase +
					60 + // We want it to switch on the trailing end of the minute
					3600; // We want it to switch an hour after the meeting should have ended

				phaseSwitchTimeout = window.setTimeout(() => setPhase("after"), secondsUntilAfterPhase * 1000);
				break;
			}
		}

		return () => {
			if (phaseSwitchTimeout) clearTimeout(phaseSwitchTimeout);
		};
	}, [meetingContextValue, calculatePhase]);

	const { phase: meetingPhase } = meetingContextValue;

	// Letting the app know which ongoing meeting was visited last
	const { setOngoingMeeting } = React.useContext(OngoingMeetingContext);
	useEffect(() => {
		if (
			note.event?.end &&
			note.url &&
			note.eventGuid &&
			note.event.status !== "cancelled" &&
			meetingPhase === "started"
		) {
			setOngoingMeeting({
				guid: note.eventGuid,
				title: note.event.title,
				url: note.url,
				end: note.event.end,
			});
		}
	}, [meetingPhase, note, setOngoingMeeting]);

	const latestMeetingRecording = note.meetingRecordings?.length
		? note.meetingRecordings[note.meetingRecordings.length - 1]
		: undefined;

	/*
	 * The recordingProvider and recordingMediaType values are determined by either:
	 * - the value of recordingMode, if the client is the one who triggered the action;
	 * - the data we know about the latest meeting recording, if the client is part of the
	 *		recording but not the one that triggered the recording controls.
	 */
	const recordingProvider = useMemo(() => {
		if (!recordingMode) return latestMeetingRecording?.externalRecordingProvider ?? "RECALLAI";

		return recordingMode === "BOTLESS" ? "CLIENT_SIDE" : "RECALLAI";
	}, [recordingMode, latestMeetingRecording?.externalRecordingProvider]);

	const recordingMediaType = useMemo(() => {
		if (!recordingMode) return latestMeetingRecording?.mediaType ?? "VIDEO";

		return recordingMode === "BOTLESS" ? "AUDIO" : "VIDEO";
	}, [recordingMode, latestMeetingRecording?.mediaType]);

	const initialStatus: CopilotStatus = useMemo(() => {
		let idleState: CopilotStatus;
		if (!note.event) {
			idleState = { type: "not-recording" };
		} else {
			const anyConferenceCallSupported = someConferenceCallSupportsRecording(note.event.conferenceCalls);

			const showScheduledStatus =
				viewer.hasAccessToScheduledMeetingRecordings &&
				note.event.status !== "cancelled" &&
				note.stream.schedulingMeetingAssistantAutomationIsActive &&
				anyConferenceCallSupported &&
				meetingPhase !== "after";

			idleState = { type: showScheduledStatus ? "scheduled" : "not-recording" } as const;
		}

		if (!latestMeetingRecording) {
			return idleState;
		} else {
			return hasOwnProperty<typeof REMOTE_STATUS_MAPPING>(REMOTE_STATUS_MAPPING, latestMeetingRecording.status)
				? REMOTE_STATUS_MAPPING[latestMeetingRecording.status]
				: latestMeetingRecording.status === "RECORDING" || latestMeetingRecording.status === "PAUSED"
					? {
							type: latestMeetingRecording.status === "RECORDING" ? "recording" : "paused",
							recordingStartedAt: new Date(latestMeetingRecording.meetingCallStart),
							// while this isn't the acutal time the status was created for the paused state, anything that comes after it
							// should still be considered newer than it. This is most useful for the recording status, so any webhooks that
							// are received out of order after it joins and starts recording don't override it on the frontend.
							statusCreatedAt: [new Date(), new Date(latestMeetingRecording.meetingCallStart)].reduce(function (a, b) {
								return a < b ? a : b;
							}),
							mediaType: recordingMediaType,
							provider: recordingProvider,
						}
					: idleState;
		}
	}, [
		note.event,
		note.stream.schedulingMeetingAssistantAutomationIsActive,
		viewer.hasAccessToScheduledMeetingRecordings,
		meetingPhase,
		recordingMediaType,
		recordingProvider,
		latestMeetingRecording,
	]);

	const initialStatusRef = useRef(initialStatus);
	useEffect(() => {
		if (initialStatusRef.current.type === "recording" && inVideocall) {
			notifyExtensionRecordingInProgress();
		}
	}, []);

	const initialErrorIndicator = useMemo(() => {
		return note.recordingErrorIndicator
			? ({
					type: note.recordingErrorIndicator.errorType === "GENERIC" ? "generic" : "cancelled",
					message: note.recordingErrorIndicator.message,
					cancelledBy: note.recordingErrorIndicator.cancelledBy,
					updatedAt: new Date(note.recordingErrorIndicator.updatedAt),
				} as CopilotErrorIndicator)
			: null;
	}, [note]);

	const [copilotStatus, setCopilotStatus] = useState<CopilotStatus>(initialStatus);
	const [previousErrorIndicator, setPreviousErrorIndicator] = useState<CopilotErrorIndicator | null>(
		initialErrorIndicator,
	);

	useAutoRefetchMeetingRecordings(note);

	const socket = useGroupEmitter(note.groupName);

	useEffect(() => {
		function onSocketMessage(message: WsMessage) {
			if (message.type !== "MEETING_RECORDER_STATUS_UPDATED") return;

			if (errorIndicatorsEnabled && (message.data.cancelledBy || message.data.userFacingErrorMessage)) {
				const indicator = {
					type: message.data.cancelledBy ? ("cancelled" as const) : ("generic" as const),
					message: message.data.userFacingErrorMessage ?? undefined,
					cancelledBy: message.data.cancelledBy ?? undefined,
					updatedAt: new Date(),
				};
				setPreviousErrorIndicator(indicator);
				setCopilotStatus({
					type: "error",
					indicator,
				});
				return;
			}

			let newStatus: CopilotStatus | undefined;
			const status = message.data.status;
			const statusCreatedAt = message.data.statusCreatedAt ? new Date(message.data.statusCreatedAt) : undefined;

			// if the current status is newer than the incoming status, ignore it
			if (copilotStatus.statusCreatedAt && statusCreatedAt && statusCreatedAt < copilotStatus.statusCreatedAt) return;

			if (status === "RECORDING_DONE") {
				newStatus = { type: "saved", statusCreatedAt: statusCreatedAt };
			} else if (status === "RECORDING" || status === "PAUSED") {
				const recordingStartedAt = latestMeetingRecording?.meetingCallStart
					? new Date(latestMeetingRecording.meetingCallStart)
					: new Date();

				newStatus = {
					type: status === "RECORDING" ? "recording" : "paused",
					recordingStartedAt,
					statusCreatedAt: statusCreatedAt ?? recordingStartedAt,
					mediaType: recordingMediaType,
					provider: recordingProvider,
				};
			} else if (hasOwnProperty(REMOTE_STATUS_MAPPING, status)) {
				newStatus = { ...REMOTE_STATUS_MAPPING[status], statusCreatedAt: statusCreatedAt };
			}

			if (!newStatus) return;

			// doing this check with a callback referencing the previous state before setting the new status ensures
			// that the comparison with the old status will always be sequential and have reference to the correct last value.
			// sometimes if 2 websockets send a message to the component, within a short duration (200ms for example), the
			// setCopilotStatus will not have been properly updated the value yet for handling the 2nd message.
			setCopilotStatus(prevStatus => {
				if (!newStatus) return prevStatus;
				if (acceptNewStatus(prevStatus, newStatus)) {
					return newStatus;
				}
				return prevStatus;
			});

			if (newStatus.type === "recording" && inVideocall) {
				notifyExtensionRecordingInProgress();
			}
		}

		socket.on("message", onSocketMessage);
		return () => socket.off("message", onSocketMessage);
	}, [
		socket,
		latestMeetingRecording,
		note,
		copilotStatus,
		setCopilotStatus,
		errorIndicatorsEnabled,
		recordingProvider,
		recordingMediaType,
	]);

	// Automatically switch from "saved" to "not-recording" after 5 seconds
	// Automatically switch from "error" to initial status after 5 seconds
	useEffect(() => {
		if (copilotStatus.type !== "saved" && copilotStatus.type !== "error") return;
		const timeout = window.setTimeout(() => setCopilotStatus({ type: "not-recording" }), 5000);
		return () => window.clearTimeout(timeout);
	}, [copilotStatus]);

	useEffect(() => {
		if (copilotStatus.type === "error" && copilotStatus.indicator.message) {
			toast.error(copilotStatus.indicator.message, { title: "Note Taker", autoClose: 5000 });
		}
	}, [copilotStatus]);

	useEffect(() => {
		if (inVideocall) {
			const wasCancelledByViewer = !!(
				copilotStatus.type == "error" &&
				copilotStatus.indicator.cancelledBy &&
				copilotStatus.indicator.cancelledBy.id == viewer.user?.id
			);

			window.parent.postMessage(
				{
					type: "MEETING_RECORDER_STATUS_UPDATED",
					status: copilotStatus,
					eventGuid: note.eventGuid,
					indicator: copilotStatus.type === "error" && errorIndicatorsEnabled ? copilotStatus.indicator : null,
					wasCancelledByViewer,
				},
				"*",
			);
		}
	}, [copilotStatus, note.eventGuid, viewer.user?.id, errorIndicatorsEnabled]);

	const paywallsEnabled = useCopilotPaywallsEnabled(viewer);
	const mustUpgradeBeforeRecording = paywallsEnabled && viewer.remainingMeetingRecordingCredits === 0;
	const autoSendToEveryoneIsOn = autoShareIsSetToEveryone(note);

	const openOutOfCreditsModal = useOpenOutOfCreditsModal();

	const startRecording = useCallback<CopilotContextSlice["startRecording"]>(
		async (
			mode = "BOT",
			call,
			{
				overrideAutoRecord = false,
				mediaType = "VIDEO",
			}: {
				overrideAutoRecord?: boolean;
				mediaType?: "AUDIO" | "VIDEO";
			} = {},
		): Promise<boolean> => {
			if (mustUpgradeBeforeRecording) {
				openOutOfCreditsModal();
				return false;
			}

			if (mode === "BOTLESS" || !call) {
				if (window.desktopRecorder) {
					window.desktopRecorder.initiateRecording({ eventId: note.event?.id, noteId: note.id });
					setRecordingMode(mode);
					return true;
				}
				toast.info("Not yet implemented.");
				return false;
			}

			const enableMessage = viewer.hasAdminAccess
				? "Please enable it in Workspace Settings."
				: "Please contact an admin to enable the setting in your workspace.";

			if (!viewer.hasAccessToCopilot) {
				setCopilotStatus({
					type: "error",
					indicator: {
						type: "generic",
						message: `Note Taker is not enabled. ${enableMessage}`,
					},
				});
				return false;
			}
			setCopilotStatus({ type: "starting" });

			dispatchAnalyticsTrack("Clicked start recording attempt", {
				location: "Note",
			});

			const hasRecordingStarted = await new Promise((resolve, reject) => {
				commitMutation<RecordMeetingMutation>(environment, {
					mutation: graphql`
						mutation MeetingPhaseContext_RecordMeetingMutation(
							$noteId: ID!
							$meetingCallUrl: String!
							$overrideAutoRecord: Boolean!
							$mediaType: RecordingMediaType
						) {
							recordMeeting(
								noteId: $noteId
								meetingCallUrl: $meetingCallUrl
								overrideAutorecord: $overrideAutoRecord
								mediaType: $mediaType
							) {
								meetingRecording {
									id
									status
								}
							}
						}
					`,
					variables: {
						noteId: note.id,
						meetingCallUrl: call.url,
						overrideAutoRecord,
						mediaType,
					},
					onCompleted: (data, errors) => {
						const recordingStatus = data.recordMeeting?.meetingRecording?.status;

						if (errors && errors.length > 0) {
							const userFacingError = getFirstUserFacingError(errors);
							if (userFacingError) {
								setCopilotStatus({
									type: "error",
									indicator: {
										type: "generic",
										message: userFacingError,
									},
								});
							} else {
								setCopilotStatus(REMOTE_STATUS_MAPPING.UNKNOWN);
							}
							dispatchAnalyticsTrack("Clicked start recording failed", { location: "Note" });
							// The user may have used up their credits in another session
							// while the websocket was disconnected and the data here has
							// become stale.
							refetchViewerInBackground();
							reject(new Error(userFacingError ?? "Unknown failure in starting recording"));
						} else if (recordingStatus) {
							dispatchAnalyticsTrack("Clicked start recording succeeded", { location: "Note" });
							setRecordingMode(mode);
							const mappedRecordingStatus = hasOwnProperty<typeof REMOTE_STATUS_MAPPING>(
								REMOTE_STATUS_MAPPING,
								recordingStatus,
							)
								? REMOTE_STATUS_MAPPING[recordingStatus]
								: REMOTE_STATUS_MAPPING.UNKNOWN;
							if (mappedRecordingStatus) setCopilotStatus(mappedRecordingStatus);
							resolve(true);
						}
						reject(new Error("Unknown failure in starting recording"));
					},
				});
			});

			if (
				hasRecordingStarted &&
				note.event?.hasUsersWithExternalEmail &&
				!autoSendToEveryoneIsOn &&
				viewer.meetingRecapAutoShareSettings?.sendTo == "ALL"
			) {
				await enableAutoShareToEveryone(note.stream.id);
			}

			return hasRecordingStarted === true;
		},
		[
			mustUpgradeBeforeRecording,
			viewer.hasAdminAccess,
			viewer.hasAccessToCopilot,
			viewer.meetingRecapAutoShareSettings?.sendTo,
			setCopilotStatus,
			note.event?.hasUsersWithExternalEmail,
			note.event?.id,
			note.id,
			note.stream.id,
			autoSendToEveryoneIsOn,
			openOutOfCreditsModal,
		],
	);

	const stopRecording = useCallback(() => {
		setCopilotStatus({ type: "stopping" });

		commitMutation<StopRecordingMutation>(environment, {
			mutation: graphql`
				mutation MeetingPhaseContext_StopMeetingRecordingMutation($noteId: ID!) {
					stopMeetingRecording(noteId: $noteId) {
						note {
							id
							hasMeetingRecordings
							meetingRecordings {
								id
								isCurrentlyRecording
							}
						}
					}
				}
			`,
			variables: {
				noteId: note.id,
			},
			onCompleted: () => {
				setRecordingMode(undefined);
				dispatchAnalyticsTrack("Recording stopped", { location: "Note" });
			},
		});
	}, [note.id, setCopilotStatus]);

	const pauseRecording = useCallback(() => {
		// TODO: There should probably be a "pausing" state first. This way, if there's an error while pausing, the user was
		//  never under the impression that the recording was paused, even for a moment. -Wal
		setCopilotStatus({
			type: "paused",
			recordingStartedAt: latestMeetingRecording?.meetingCallStart
				? new Date(latestMeetingRecording.meetingCallStart)
				: new Date(),
			mediaType: recordingMediaType,
			provider: recordingProvider,
		});

		commitMutation<PauseRecordingMutation>(environment, {
			mutation: graphql`
				mutation MeetingPhaseContext_PauseMeetingRecordingMutation($noteId: ID!) {
					pauseMeetingRecording(noteId: $noteId) {
						meetingRecordings {
							id
							isCurrentlyRecording
						}
					}
				}
			`,
			variables: {
				noteId: note.id,
			},
			onCompleted: () => {
				dispatchAnalyticsTrack("Recording paused", {
					location: "Note",
					noteId: note.id,
					recordingId: latestMeetingRecording?.id,
				});
			},
		});
	}, [latestMeetingRecording, note.id, setCopilotStatus, recordingMediaType, recordingProvider]);

	const resumeRecording = useCallback(() => {
		setCopilotStatus({
			type: "recording",
			recordingStartedAt: latestMeetingRecording?.meetingCallStart
				? new Date(latestMeetingRecording.meetingCallStart)
				: new Date(),
			mediaType: recordingMediaType,
			provider: recordingProvider,
		});

		commitMutation<ResumeRecordingMutation>(environment, {
			mutation: graphql`
				mutation MeetingPhaseContext_ResumeMeetingRecordingMutation($noteId: ID!) {
					resumeMeetingRecording(noteId: $noteId) {
						meetingRecordings {
							id
							isCurrentlyRecording
						}
					}
				}
			`,
			variables: {
				noteId: note.id,
			},
			onCompleted: () => {
				dispatchAnalyticsTrack("Recording resumed", {
					location: "Note",
					noteId: note.id,
					recordingId: latestMeetingRecording?.id,
				});
			},
		});
	}, [latestMeetingRecording, note.id, setCopilotStatus, recordingMediaType, recordingProvider]);

	const contextValue: ContextValue = useMemo(
		() => ({
			...meetingContextValue,
			copilotStatus,
			previousErrorIndicator,
			viewerId: viewer.user?.id,
			startRecording,
			stopRecording,
			pauseRecording,
			resumeRecording,
			hasMeetingRecordings: note.hasMeetingRecordings,
			mustUpgradeBeforeRecording,
			creator: latestMeetingRecording?.userWhoStartedRecording,
		}),
		[
			meetingContextValue,
			copilotStatus,
			previousErrorIndicator,
			viewer.user?.id,
			startRecording,
			stopRecording,
			pauseRecording,
			resumeRecording,
			note.hasMeetingRecordings,
			mustUpgradeBeforeRecording,
			latestMeetingRecording?.userWhoStartedRecording,
		],
	);

	return <MeetingPhaseContext.Provider value={contextValue}>{children}</MeetingPhaseContext.Provider>;
}
